import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

async function apiError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { ok?: false; error?: { message?: string; code?: string } };
    const msg = body?.error?.message ?? body?.error?.code ?? res.statusText;
    return new Error(msg ? `${res.status}: ${msg}` : `Failed: ${res.status}`);
  } catch {
    return new Error(text || res.statusText || `Failed: ${res.status}`);
  }
}

const REFRESH_KEY = 'ops.refresh_token';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Invalid invite link.');
      return;
    }
    fetch(`/v1/auth/invite/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) throw await apiError(res);
        const body = await res.json() as { ok: true; data: { email: string; displayName: string; expiresAt: string } };
        setEmail(body.data.email);
        setValid(true);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Invite not found or expired.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) {
      setError('Invalid invite link.');
      return;
    }
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setAccepting(true);
    setError(null);
    try {
      const res = await fetch(`/v1/auth/invite/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (!res.ok) throw await apiError(res);
      const body = await res.json() as {
        ok: true;
        data: {
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
          user: { id: string; email: string; displayName: string; role: string };
        };
      };
      localStorage.setItem(REFRESH_KEY, body.data.refreshToken);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invite.');
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base text-white">
        <p className="text-muted">Verifying invite…</p>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base text-white px-4">
        <div className="bg-bg-panel border border-border rounded-xl p-5 sm:p-6 w-full max-w-md text-center">
          <h1 className="text-lg font-semibold mb-2">Invite not valid</h1>
          <p className="text-sm text-rose-400">{error ?? 'This invite link is invalid or has expired.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base text-white px-4">
      <div className="bg-bg-panel border border-border rounded-xl p-5 sm:p-6 w-full max-w-md">
        <h1 className="text-lg sm:text-xl font-semibold mb-1">Accept invitation</h1>
        <p className="text-sm text-muted mb-4">Set a password for {email ?? 'your account'}.</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs sm:text-sm text-muted mb-1">Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              required
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm text-white focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm text-muted mb-1">Confirm password *</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm text-white focus:outline-none focus:border-accent"
            />
          </div>
          {error && <p className="text-xs sm:text-sm text-rose-400">{error}</p>}
          <button
            type="submit"
            disabled={accepting || !password || !confirm}
            className="w-full bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm sm:text-base font-medium px-4 py-3 sm:py-2 min-h-[44px] rounded-lg"
          >
            {accepting ? 'Setting up…' : 'Accept & sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
